import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "../../css/Homepage.css";
import {
  formatDate,
  getImageUrl,
  processHTML,
  sanitizeHTML,
} from "../../utils/portfolioUtils";
import LoadingState from "../common/LoadingState";
import ArxivButton from "./buttons/ArxivButton";
import GithubButton from "./buttons/GithubButton";
import SlidesButton from "./buttons/SlidesButton";

const SOCIAL = {
  github: "https://github.com/kaustubhharapanahalli",
  linkedin: "https://linkedin.com/in/kmhalli",
  scholar: "https://scholar.google.com/citations?user=51Dqq8kAAAAJ&hl=en",
};

const Home = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const [homepageData, setHomepageData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleImageLoad = () => setImageLoaded(true);
  const handleMouseUp = (e) => e.target.classList.remove("active");

  const handleCopyEmail = () => {
    if (!homepageData?.email) return;
    navigator.clipboard.writeText(homepageData.email);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/homepage/`,
        );
        setHomepageData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching homepage data:", err);
        setRetryCount((prev) => prev + 1);
      }
    };

    fetchHomepageData();

    const retryTimer = setTimeout(() => {
      if (loading && retryCount < 5) {
        fetchHomepageData();
      } else if (retryCount >= 5) {
        setLoading(false);
        setError(true);
      }
    }, 5000);

    return () => clearTimeout(retryTimer);
  }, [loading, retryCount]);

  if (loading || (!homepageData && !error)) {
    return <LoadingState />;
  }

  if (error || !homepageData) {
    return (
      <section className="home-section">
        <Container className="mt-5 text-center">
          <p style={{ color: "var(--text-secondary)" }}>
            Couldn&apos;t load content — please try refreshing the page.
          </p>
        </Container>
      </section>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: homepageData.name,
    jobTitle: homepageData.current_position,
    affiliation: { "@type": "Organization", name: homepageData.institution },
    email: homepageData.email,
    sameAs: [SOCIAL.github, SOCIAL.linkedin, SOCIAL.scholar],
  };

  const displayEmail = homepageData.email
    .replace("@", " [at] ")
    .replace(/\.(?=[^.]*$)/, " [dot] ");

  return (
    <section className="home-section">
      <Helmet>
        <title>
          {homepageData.name} — {homepageData.current_position}
        </title>
        <meta
          name="description"
          content={`Portfolio of ${homepageData.name}, ${homepageData.current_position} at ${homepageData.institution}.`}
        />
        <meta property="og:title" content={homepageData.name} />
        <meta
          property="og:description"
          content={`${homepageData.current_position} at ${homepageData.institution}.`}
        />
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Container>
        <div className="home-layout">
          {/* Sticky sidebar */}
          <aside className="author-sidebar">
            <img
              ref={imageRef}
              src={getImageUrl(homepageData.profile_image)}
              alt={homepageData.name}
              className={`author-avatar ${!imageLoaded ? "loading" : ""}`}
              onLoad={handleImageLoad}
              loading="lazy"
            />

            <div className="author-info">
              <h1 className="author-name">{homepageData.name}</h1>
              <p className="author-position">{homepageData.current_position}</p>
              <p className="author-institution">{homepageData.school}</p>
              <p className="author-institution">{homepageData.institution}</p>
            </div>

            <div className="author-contact">
              <div className="author-email-row">
                <a
                  href={`mailto:${homepageData.email}`}
                  className="author-email"
                >
                  {displayEmail}
                </a>
                <div className="copy-btn-wrapper">
                  <button
                    className="copy-btn"
                    onClick={handleCopyEmail}
                    aria-label="Copy email address"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/Assets/images/copy.png`}
                      alt=""
                      aria-hidden="true"
                      className="copy-icon"
                    />
                  </button>
                  {copySuccess && (
                    <div
                      className="copy-success-popup"
                      role="status"
                      aria-live="polite"
                    >
                      Copied!
                    </div>
                  )}
                </div>
              </div>

              <div className="social-icons">
                <a
                  href={SOCIAL.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub profile"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/images/github.png`}
                    alt="GitHub"
                    className="icon"
                  />
                </a>
                <a
                  href={SOCIAL.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn profile"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/images/linkedin.png`}
                    alt="LinkedIn"
                    className="icon"
                  />
                </a>
                <a
                  href={SOCIAL.scholar}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google Scholar profile"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/images/googlescholar.svg`}
                    alt="Google Scholar"
                    className="icon"
                  />
                </a>
              </div>
            </div>

            <a
              href={homepageData.resume_file}
              download={`${homepageData.name}_Resume.pdf`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="download-btn sidebar-cv-btn"
                onMouseUp={handleMouseUp}
              >
                Download CV
              </Button>
            </a>
          </aside>

          {/* Main content */}
          <main className="home-main">
            {/* About */}
            <div
              className="content-bio"
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(homepageData.about_text),
              }}
            />

            {/* Updates */}
            <div className="content-section">
              <h2 className="section-heading">Updates</h2>
              <div className="updates-list">
                {homepageData.updates.map((update, index) => (
                  <div key={index} className="update-row">
                    <span className="update-date">
                      {formatDate(update.date)}
                    </span>
                    <div
                      className="update-body"
                      dangerouslySetInnerHTML={{
                        __html: processHTML(update.content),
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="section-footer">
                <Link to="/news">
                  <Button className="download-btn" onMouseUp={handleMouseUp}>
                    Read More News
                  </Button>
                </Link>
              </div>
            </div>

            {/* Publications */}
            <div className="content-section">
              <h2 className="section-heading">Publications</h2>
              {homepageData.publications.map((publication, index) => (
                <div key={index} className="pub-row">
                  {publication.thumbnail && (
                    <a
                      href={publication.arxiv_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pub-thumb-link"
                    >
                      <img
                        src={getImageUrl(publication.thumbnail)}
                        alt={publication.title}
                        className="pub-thumbnail"
                      />
                    </a>
                  )}
                  <div className="pub-details">
                    <p className="paper-title">
                      <a
                        href={publication.arxiv_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="paper-link"
                      >
                        {publication.title}
                      </a>
                    </p>
                    <p className="pub-venue">
                      {publication.venue_link ? (
                        <a
                          href={publication.venue_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="paper-link"
                        >
                          {publication.venue}
                        </a>
                      ) : (
                        publication.venue
                      )}
                    </p>
                    <p className="authors">
                      {publication.authors}
                      {publication.shared_first_author && (
                        <small className="footnote">
                          <sup>*</sup> - shared first authors
                        </small>
                      )}
                    </p>
                    <div className="paper-buttons">
                      {publication.arxiv_link && (
                        <ArxivButton link={publication.arxiv_link} />
                      )}
                      {publication.github_link && (
                        <GithubButton link={publication.github_link} />
                      )}
                      {publication.slides_file && (
                        <SlidesButton file={publication.slides_file} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </Container>
    </section>
  );
};

export default Home;
